<template>
  <div class="corr-wrap">
    <!-- El shadow root cuelga de acá; su contenido lo maneja este componente
         con DOM plano (ver shadowMount.js). SIN overflow:hidden: la historia
         usa position:sticky y un ancestro que recorta la apaga. -->
    <div ref="host" class="correccion-host"></div>
    <p v-if="error" class="corr-error">
      No pudimos cargar el detalle de Corrección de Tests. Inicia sesión y
      corrige tu próximo test: los primeros informes son gratis.
    </p>
  </div>
</template>

<script>
// El cuerpo de /correccion-de-tests/ dentro del chatbot demo: la
// historia del porqué (avanza con el scroll), lo que hace —cinco flujos de
// ingreso, cada uno terminado en la captura de su informe en Word— y el
// selector de los test.
//
// Mismo marcado y mismas hojas que la página (el endpoint renderiza el parcial
// marketing/_correccion_secciones.html), montados en un shadow root. Los
// animadores de abajo son port de los scripts de la página, y los datos que
// escriben son parte de esos guiones: dos relojes, un solo contenido servido.
import informationService from "@/services/informationService";
import {
  montarEnSombra,
  saltosInternos,
  entradas,
  selector,
} from "./shadowMount";

// Lo que cambia respecto de la página. Va después de las hojas.
const AJUSTES =
  // Los márgenes de sección del sitio están pensados para una página entera.
  ".section{padding-block:1.75rem}" +
  ".shell{max-width:none;padding-inline:1.25rem}" +
  // Los "inicia sesión" del bloque: el menú del chat ya trae el suyo.
  ".btn-row{display:none!important}";

// --- los guiones de las animaciones (copiados del script de la página) -----
const TIPO = ["Informe_WISC5", "Informe_TEPROSIF", "Informe_SENSORIAL", "Informe_STSG", "Informe_TECAL", "Informe_IDTEL", "Informe_ICAP", "Informe_WAIS4", "Informe_Unificado_fono", "Informe_Unificado_psico"];
const NOMB = ["Emilia_Rojas", "Tomas_Perez", "Antonia_Diaz", "Benjamin_Soto", "Martina_Lopez", "Vicente_Munoz", "Sofia_Castro", "Matias_Reyes", "Isidora_Vega", "Lucas_Araya", "Florencia_Silva", "Agustin_Torres"];
const ANALISIS =
  "Perfil heterogéneo: 50 puntos entre el índice más alto y el más bajo. El CI Total debe interpretarse con cautela; se recomienda priorizar la lectura por índices.";
// La lupa de la tabla de baremos: el escalar que "encuentra" en cada fila.
const ESC = [3, 9, 7, 4, 2, 6, 5, 17, 2, 5, 7, 12];
const TEPROSIF = ["piancha", "likotero", "afiomba", "nono", "tien"];
// [puntaje, corte] — los del informe de muestra.
const IDTEL = [[12, 17], [17, 17], [27, 22], [6, 10], [62, 66]];
// Los puntajes brutos del ejemplo que trae el producto.
const WISC = [24, 21, 15, 15, 21, 13, 21, 13, 12, 36, 9, 16, 13, 55, 18];
const ICAP = [0, 1, 0, 1]; // cómo realiza cada actividad
const ICAP_LOGRO = [38, 80, 40, 40]; // % de logro por escala
const SENSORIAL = [4, 5, 5, 4];

const fmt = (n) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");

// Marca el valor n de una escala 0..N (o ninguno, con -1).
const pick = (wrap, n) => {
  if (wrap) Array.from(wrap.children).forEach((b, i) => b.classList.toggle("sel", i === n));
};

// Cuenta de 0 a `fin` con salida suave.
const contar = (el, fin, ms, sufijo = "") => {
  const t0 = performance.now();
  const f = () => {
    const p = Math.min(1, (performance.now() - t0) / ms);
    el.textContent = `${Math.round(fin * (1 - Math.pow(1 - p, 3)))}${sufijo}`;
    if (p < 1) requestAnimationFrame(f);
  };
  f();
};

// El cursor de mentira: se coloca sobre un elemento, en coordenadas del panel.
const cursor = (c, host, el, dx, dy) => {
  if (!c || !el) return;
  const a = host.getBoundingClientRect();
  const b = el.getBoundingClientRect();
  c.classList.add("on");
  c.style.transform = `translate(${b.left - a.left + (dx === undefined ? b.width - 14 : dx)}px,${
    b.top - a.top + (dy === undefined ? b.height / 2 - 4 : dy)
  }px)`;
};

export default {
  name: "CorreccionSecciones",
  data() {
    return { error: false };
  },
  mounted() {
    informationService
      .getCorreccionSecciones()
      .then((payload) => this.montar(payload))
      .catch(() => {
        this.error = true;
      });
  },
  beforeDestroy() {
    (this.limpiezas || []).forEach((fn) => fn());
  },
  methods: {
    montar(payload) {
      const montado = montarEnSombra(this.$refs.host, payload, {
        ajustes: AJUSTES,
      });
      if (!montado) {
        this.error = true;
        return;
      }
      this.raiz = montado.raiz;
      this.$id = (id) => this.raiz.getElementById(id);

      // `js` es la clase con la que las hojas apagan lo que las animaciones
      // encienden. Sin movimiento no se pone: todo queda quieto y completo
      // (el marcado viene servido con el estado final).
      const conMovimiento =
        !!window.IntersectionObserver &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      this.limpiezas = [saltosInternos(this.raiz), selector(this.raiz, conMovimiento)];
      // Las pestañas de los informes andan con o sin movimiento; el bucle las
      // cambia solo mientras nadie las haya tocado.
      this.pestanas = {
        wisc: this.pestana(["w-t1", "w-t2"], ["w-img1", "w-img2"]),
        sensorial: this.pestana(["s-esc", "s-pad"], ["s-img-esc", "s-img-pad"], (n) => {
          this.$id("s-cual").textContent = n === 0 ? "Cuestionario escolar" : "Cuestionario para padres";
        }),
      };
      if (!conMovimiento) return;
      montado.cuerpo.classList.add("js");
      this.limpiezas.push(entradas(this.raiz));
      const ut = this.tecleo();
      this.historia(ut);
      this.animaciones(ut);
    },

    pestana(botones, imagenes, alCambiar) {
      const $ = this.$id;
      if (!botones.concat(imagenes).every((id) => $(id))) {
        return { manual: true, ver() {} };
      }
      const t = {
        manual: false,
        ver(n) {
          botones.forEach((id, i) => $(id).classList.toggle("on", i === n));
          imagenes.forEach((id, i) => $(id).classList.toggle("off", i !== n));
          if (alCambiar) alCambiar(n);
        },
      };
      botones.forEach((id, i) =>
        $(id).addEventListener("click", () => {
          t.manual = true;
          t.ver(i);
        })
      );
      return t;
    },

    // --- motor de tecleo -----------------------------------------------------
    // Una sola cola a 16 ms en vez de un setInterval por campo.
    tecleo() {
      let JOBS = [];
      const reloj = setInterval(() => {
        if (!JOBS.length) return;
        const now = performance.now();
        JOBS.forEach((j) => {
          const n = Math.min(j.s.length, Math.max(0, Math.floor((now - j.t) / j.d)));
          if (n !== j.n) {
            j.n = n;
            j.el.textContent = j.s.slice(0, n);
            if (n === j.s.length) j.end();
          }
        });
        JOBS = JOBS.filter((j) => j.n < j.s.length);
      }, 16);
      this.limpiezas.push(() => clearInterval(reloj));
      return {
        type(el, s, ms, end) {
          if (!el) return;
          JOBS = JOBS.filter((j) => j.el !== el);
          el.textContent = "";
          el.classList.add("typing");
          JOBS.push({
            el,
            s,
            t: performance.now(),
            d: Math.max(12, ms / s.length),
            n: 0,
            end() {
              el.classList.remove("typing");
              if (end) end();
            },
          });
        },
        clear(el) {
          if (!el) return;
          JOBS = JOBS.filter((j) => j.el !== el);
          el.textContent = "";
          el.classList.remove("typing");
        },
      };
    },

    // --- la historia del porqué, contada con el scroll ----------------------
    historia(ut) {
      const $ = this.$id;
      const story = $("story");
      if (!story) return;
      const steps = Array.from(story.querySelectorAll(".story__step"));
      const FILES = [];
      for (let k = 0; k < 40; k++) {
        FILES.push(`${TIPO[(k * 3) % TIPO.length]}_${NOMB[(k * 7) % NOMB.length]}.docx`);
      }
      $("sflb").innerHTML = FILES.map(
        (f, k) =>
          `<div class="story__fr" id="sfr${k}"><i></i><span>${f}</span><em>${
            8 + (k % 20)
          } nov · ${38 + ((k * 7) % 60)} KB</em></div>`
      ).join("");

      const lupa = $("blupa");
      const grid = story.querySelector(".bar__grid");
      let hit = null;
      if (grid) {
        hit = document.createElement("span");
        hit.className = "bar__hit";
        grid.parentNode.appendChild(hit);
      }

      let cur = -1;
      let timers = [];
      const at = (t, f) => timers.push(setTimeout(f, t));
      const bump = (v) => {
        const n = $("snum");
        n.textContent = v;
        n.classList.add("bump");
        at(220, () => n.classList.remove("bump"));
      };
      const countFrom = (el, a, b, ms, guard) => {
        const t0 = performance.now();
        const f = () => {
          const p = Math.min(1, (performance.now() - t0) / ms);
          const e = 1 - Math.pow(1 - p, 3);
          el.textContent = fmt(a + (b - a) * e);
          if (p < 1 && cur === guard) requestAnimationFrame(f);
        };
        f();
      };
      const sweep = () => {
        if (!grid || !lupa) return;
        const first = grid.children[6];
        if (!first || !first.offsetHeight) return;
        const h = first.offsetHeight;
        const top = first.offsetTop;
        lupa.style.top = `${top}px`;
        lupa.style.height = `${h}px`;
        ESC.forEach((v, k) =>
          at(260 + k * 520, () => {
            lupa.style.transform = `translateY(${k * h}px)`;
            hit.classList.remove("on");
            at(230, () => {
              hit.style.top = `${top + k * h + 1}px`;
              hit.style.right = ".9rem";
              hit.textContent = `escalar ${v}`;
              hit.classList.add("on");
            });
          })
        );
      };

      const setBeat = (i) => {
        if (i === cur) return;
        cur = i;
        story.setAttribute("data-beat", i);
        steps.forEach((s, k) => s.classList.toggle("on", k === i));
        timers.forEach(clearTimeout);
        timers = [];
        if (hit) hit.classList.remove("on");
        const num = $("snum");
        const lab = $("slab");
        if (i === 1) {
          num.textContent = "15";
          lab.textContent = "números sacados de una tabla";
          lab.classList.remove("dark");
          at(1200, () => bump("26"));
          at(2300, () => bump("35"));
          sweep();
        }
        if (i === 2) {
          num.textContent = "40";
          lab.textContent = "informes en el año";
          lab.classList.add("dark");
          const fb = $("sflb");
          const fw = $("sflw");
          fb.style.transform = "";
          $("sflc").textContent = "";
          FILES.forEach((f, k) => $(`sfr${k}`).classList.remove("on"));
          at(300, () => countFrom(num, 40, 280, 2800, 2));
          FILES.forEach((f, k) =>
            at(250 + k * 85, () => {
              const r = $(`sfr${k}`);
              r.classList.add("on");
              $("sflc").textContent = `${k + 1} informes`;
              const rh = r.offsetHeight || 30;
              const vis = Math.max(3, Math.floor(fw.clientHeight / rh));
              if (k >= vis) fb.style.transform = `translateY(${-(k - vis + 1) * rh}px)`;
            })
          );
        }
        const t = $("inftxt");
        if (i === 4) {
          if (t) {
            t.classList.remove("done");
            at(500, () => ut.type(t, ANALISIS, 2600, () => t.classList.add("done")));
          }
        } else {
          ut.clear(t);
        }
      };

      // El paso activo lo decide el scroll: en escritorio, el que cruza el
      // centro de la pantalla; en teléfono, el que asoma bajo el marco fijo.
      // Se escucha en captura sobre el documento para enterarse aunque quien
      // se desplace sea un contenedor del SPA y no la ventana.
      const stage = story.querySelector(".story__stage");
      let tick = false;
      const elegir = () => {
        tick = false;
        const H = window.innerHeight;
        let line;
        if (window.matchMedia("(max-width:900px)").matches) {
          const fb = stage.getBoundingClientRect().bottom;
          line = fb + (H - fb) * 0.42;
        } else {
          line = H * 0.5;
        }
        let k = 0;
        steps.forEach((s, i) => {
          if (s.getBoundingClientRect().top < line) k = i;
        });
        setBeat(k);
      };
      const onScroll = () => {
        if (!tick) {
          tick = true;
          requestAnimationFrame(elegir);
        }
      };
      document.addEventListener("scroll", onScroll, { passive: true, capture: true });
      window.addEventListener("resize", onScroll);
      elegir();
      this.limpiezas.push(() => {
        document.removeEventListener("scroll", onScroll, { capture: true });
        window.removeEventListener("resize", onScroll);
        timers.forEach(clearTimeout);
      });
    },

    // --- las cinco animaciones de producto ------------------------------------
    // Cada una es una lista de tiempos que se repite mientras el bloque está en
    // pantalla y se detiene cuando sale. Todas terminan igual: `.listo` apaga
    // el ingreso y muestra la captura del informe.
    animaciones(ut) {
      const $ = this.$id;
      const bucle = (host, dur, paso) => {
        if (!host) return;
        let timers = [];
        let alive = false;
        const at = (t, f) => timers.push(setTimeout(f, t));
        const limpiar = () => {
          timers.forEach(clearTimeout);
          timers = [];
        };
        const correr = () => {
          limpiar();
          paso(at, host);
          at(dur, () => {
            if (alive) correr();
          });
        };
        const io = new IntersectionObserver(
          (es) =>
            es.forEach((e) => {
              if (e.isIntersecting) {
                if (!alive) {
                  alive = true;
                  correr();
                }
              } else {
                alive = false;
                limpiar();
              }
            }),
          { threshold: 0.25 }
        );
        io.observe(host);
        this.limpiezas.push(() => {
          io.disconnect();
          limpiar();
        });
      };

      // 01 · TEPROSIF-R
      bucle($("vg1"), 12500, (at, host) => {
        const c = $("e-cur");
        const psf = $("psf");
        const psfn = $("psfn");
        host.classList.remove("listo");
        psf.classList.remove("on");
        psfn.textContent = "0";
        TEPROSIF.forEach((d, i) => {
          ut.clear($(`e${i}s`));
          $(`e${i}`).classList.remove("lit");
          Array.from($(`e${i}c`).children).forEach((s) => s.classList.remove("on"));
        });
        TEPROSIF.forEach((d, i) => {
          const t0 = 260 + i * 470;
          const row = $(`e${i}`);
          at(t0 - 110, () => cursor(c, host, row, 60, 4));
          at(t0, () => {
            row.classList.add("lit");
            ut.type($(`e${i}s`), d, 280);
          });
          at(t0 + 310, () =>
            Array.from($(`e${i}c`).children).forEach((s, k) =>
              at(k * 55, () => s.classList.add("on"))
            )
          );
          at(t0 + 440, () => row.classList.remove("lit"));
        });
        at(2640, () => {
          c.classList.remove("on");
          psf.classList.add("on");
          contar(psfn, 68, 650);
        });
        at(3450, () => host.classList.add("listo"));
      });

      // 02 · IDTEL, y los otros tres en el mismo informe
      bucle($("vg2"), 11000, (at, host) => {
        const c = $("k-cur");
        const chips = Array.from($("kmix").querySelectorAll("span:not(.lab)"));
        const flag = (i) => {
          const u = $(`k${i}f`);
          const bajo = IDTEL[i][0] <= IDTEL[i][1];
          u.textContent = bajo ? "bajo el corte" : "sobre el corte";
          u.className = bajo ? "bajo on" : "sobre on";
        };
        host.classList.remove("listo");
        IDTEL.forEach((m, i) => {
          $(`k${i}v`).textContent = "—";
          $(`k${i}`).classList.remove("lit");
          const u = $(`k${i}f`);
          u.textContent = "";
          u.className = "";
        });
        chips.forEach((s) => s.classList.remove("on"));
        IDTEL.forEach((m, i) => {
          const t0 = 260 + i * 300;
          const row = $(`k${i}`);
          at(t0 - 95, () => {
            if (i < 4) cursor(c, host, $(`k${i}v`), undefined, 4);
          });
          at(t0, () => {
            row.classList.add("lit");
            $(`k${i}v`).textContent = m[0];
          });
          at(t0 + 160, () => flag(i));
          at(t0 + 270, () => row.classList.remove("lit"));
        });
        at(1700, () => c.classList.remove("on"));
        chips.forEach((s, k) => at(1780 + k * 140, () => s.classList.add("on")));
        at(2600, () => host.classList.add("listo"));
      });

      // 03 · WISC-V y WAIS-IV
      bucle($("vg3"), 15000, (at, host) => {
        const c = $("w-cur");
        const btn = $("w-btn");
        const br = $("w-br");
        const es = $("w-es");
        const tipo = $("w-tipo");
        const tabs = this.pestanas.wisc;
        host.classList.remove("listo");
        btn.classList.remove("press");
        WISC.forEach((v, i) => {
          $(`w${i}v`).textContent = "—";
          $(`w${i}`).classList.remove("lit");
        });
        br.classList.add("on");
        es.classList.remove("on");
        tipo.textContent = "Puntaje bruto";
        if (!tabs.manual) tabs.ver(0);
        // El tipo de puntaje: primero se muestra que se puede elegir.
        at(280, () => {
          br.classList.remove("on");
          es.classList.add("on");
          tipo.textContent = "Puntaje estándar";
        });
        at(880, () => {
          es.classList.remove("on");
          br.classList.add("on");
          tipo.textContent = "Puntaje bruto";
        });
        WISC.forEach((v, i) => {
          const t0 = 1250 + i * 135;
          const row = $(`w${i}`);
          at(t0 - 70, () => cursor(c, host, $(`w${i}v`), undefined, 4));
          at(t0, () => {
            row.classList.add("lit");
            $(`w${i}v`).textContent = v;
          });
          at(t0 + 200, () => row.classList.remove("lit"));
        });
        at(3400, () => cursor(c, host, btn));
        at(3750, () => btn.classList.add("press"));
        at(4000, () => {
          btn.classList.remove("press");
          c.classList.remove("on");
        });
        at(4400, () => host.classList.add("listo"));
        // Y el informe se muestra en sus dos versiones, salvo que alguien haya
        // tocado una pestaña: ahí manda la persona.
        at(9200, () => {
          if (!tabs.manual) tabs.ver(1);
        });
      });

      // 04 · ICAP
      bucle($("vg4"), 13000, (at, host) => {
        const c = $("c-cur");
        host.classList.remove("listo");
        ICAP.forEach((v, i) => {
          pick($(`c${i}s`), -1);
          $(`ci${i}`).style.width = "0%";
          $(`cb${i}`).textContent = "0%";
        });
        ICAP.forEach((v, i) => {
          const t0 = 380 + i * 380;
          at(t0 - 130, () => cursor(c, host, $(`c${i}s`).children[v], -2, 4));
          at(t0, () => pick($(`c${i}s`), v));
        });
        at(2000, () => {
          c.classList.remove("on");
          ICAP_LOGRO.forEach((v, i) => {
            $(`ci${i}`).style.width = `${v}%`;
            contar($(`cb${i}`), v, 750, "%");
          });
        });
        at(3400, () => host.classList.add("listo"));
      });

      // 05 · Perfil Sensorial 2
      bucle($("vg5"), 14000, (at, host) => {
        const c = $("s-cur");
        const tabs = this.pestanas.sensorial;
        host.classList.remove("listo");
        SENSORIAL.forEach((v, i) => pick($(`q${i}s`), -1));
        if (!tabs.manual) tabs.ver(0);
        SENSORIAL.forEach((v, i) => {
          const t0 = 380 + i * 400;
          at(t0 - 130, () => cursor(c, host, $(`q${i}s`).children[v], -2, 4));
          at(t0, () => pick($(`q${i}s`), v));
        });
        at(2000, () => c.classList.remove("on"));
        at(2600, () => host.classList.add("listo"));
        at(8000, () => {
          if (!tabs.manual) tabs.ver(1);
        });
      });
    },
  },
};
</script>

<style scoped>
.corr-wrap {
  width: 100%;
}

/* Sin overflow:hidden a propósito: recortar acá apaga el position:sticky de
   la historia. Las esquinas redondeadas las pone el contenido. */
.correccion-host {
  display: block;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 24px 46px rgba(57, 62, 70, 0.15);
}

.corr-error {
  margin: 0;
  padding: 1.2rem 1.4rem;
  color: #5c636d;
  text-align: left;
}
</style>
